import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import Typography from '@mui/material/Typography';

import CardPagination from '~/components/CardPagination';
import type {
  DataTableCardPaginationProps,
  DataTableColumn,
  DataTablePrimaryAction,
  DataTableSearchConfig,
  DataTableSortConfig,
  SortableType,
  SortState,
} from '~/components/DataView/DataTable/utils/types';
import TableRowsLoader from '~/components/VisionUI/TableRowsLoader';
import useResponsive from '~/hooks/useResponsive';
import { DEFAULT_ROWS_PER_PAGE } from '~/utils/common/constants';

import DataTableActions from './components/DataTableActions';
import DataTableBody from './components/DataTableBody';
import DataTableHeader from './components/DataTableHeader';
import { compareSortableValues, normalizeSortableValue } from './utils/helper';

interface DataTableCardProps<TRow extends Record<string, unknown>> {
  columns: Array<DataTableColumn<TRow>>;
  rows?: TRow[];
  search?: DataTableSearchConfig | null;
  primaryAction?: DataTablePrimaryAction;
  cardPagination?: DataTableCardPaginationProps;
  sort?: DataTableSortConfig;
  getRowId?: (row: TRow, index: number) => string | number;
  emptyState?: ReactNode;
  isError?: boolean;
  isLoading?: boolean;
  loadingRowCount?: number;
  stickyHeader?: boolean;
  'data-testid'?: string;
  sx?: SxProps<Theme>;
}

const DataTableCard = <TRow extends Record<string, unknown>>({
  columns,
  rows = [],
  search = null,
  primaryAction = undefined,
  cardPagination = undefined,
  sort = undefined,
  getRowId = (_row: TRow, index: number): string | number => index,
  emptyState = null,
  isError = false,
  isLoading = false,
  loadingRowCount = 3,
  stickyHeader = true,
  'data-testid': dataTestId,
  ...props
}: DataTableCardProps<TRow>) => {
  const { t } = useTranslation('common');

  const { isMobile } = useResponsive();
  const isMobileLayout = isMobile();

  const displayedColumns = useMemo(() => {
    if (!isMobileLayout) {
      return columns;
    }

    /* On mobile, allow columns to opt into visibility via an optional `mobileVisible` flag.
    If no columns specify `mobileVisible`, fall back to showing only the first and last
     columns to keep the table compact.
     */
    const mobileConfiguredColumns = columns.filter(
      (column) => column.mobileVisible,
    );

    if (mobileConfiguredColumns.length > 0) {
      return mobileConfiguredColumns;
    }

    const lastIndex = columns.length - 1;
    return columns.filter((_, index) => index === 0 || index === lastIndex);
  }, [columns, isMobileLayout]);

  const isControlledSort = Boolean(sort?.onSort);
  const [internalSortState, setInternalSortState] = useState<SortState | null>(
    () =>
      sort?.orderBy
        ? {
            order: sort.order ?? 'asc',
            orderBy: sort.orderBy,
          }
        : null,
  );

  useEffect(() => {
    if (isControlledSort || !sort?.orderBy) {
      return;
    }

    setInternalSortState({
      order: sort.order ?? 'asc',
      orderBy: sort.orderBy,
    });
  }, [isControlledSort, sort?.order, sort?.orderBy]);

  const activeSort = useMemo(() => {
    if (isControlledSort) {
      if (sort?.orderBy) {
        return { order: sort.order ?? 'asc', orderBy: sort.orderBy };
      }

      return null;
    }

    return internalSortState;
  }, [internalSortState, isControlledSort, sort?.order, sort?.orderBy]);

  const searchPlaceholder: string =
    search?.placeholder ?? t('dataTable.searchPlaceholder');
  const shouldRenderHeader = Boolean(search) || Boolean(primaryAction);

  const resolvedEmptyState = useMemo(
    () =>
      emptyState ?? (
        <Stack alignItems="center" spacing={1}>
          {isError ? (
            <Typography color="error">{t('dataTable.error')}</Typography>
          ) : (
            <Typography color="text.secondary">
              {t('dataTable.empty')}
            </Typography>
          )}
        </Stack>
      ),
    [emptyState, isError, t],
  );

  const handleSortRequest = (columnId: string) => {
    const isSameColumn = activeSort?.orderBy === columnId;
    const nextOrder: 'asc' | 'desc' =
      isSameColumn && activeSort?.order === 'asc' ? 'desc' : 'asc';

    if (sort?.onSort) {
      sort.onSort(columnId, nextOrder);
      return;
    }

    setInternalSortState({ order: nextOrder, orderBy: columnId });
  };

  const sortedRows = useMemo(() => {
    if (!activeSort?.orderBy) {
      return rows;
    }

    const sortableColumn = columns.find(
      (column) => column.sortable && String(column.id) === activeSort.orderBy,
    );

    if (!sortableColumn) {
      return rows;
    }

    const resolveValue =
      sortableColumn.sortAccessor ??
      ((row: TRow) => row[sortableColumn.id as keyof TRow] as SortableType);

    return [...rows].sort((a, b) => {
      const aValue = normalizeSortableValue(resolveValue(a));
      const bValue = normalizeSortableValue(resolveValue(b));
      const comparison = compareSortableValues(aValue, bValue);
      return activeSort.order === 'asc' ? comparison : -comparison;
    });
  }, [activeSort, columns, rows]);

  const shouldShowEmptyState = !isLoading && sortedRows.length === 0;
  const shouldShowPagination =
    (cardPagination?.resultCount ?? 0) > DEFAULT_ROWS_PER_PAGE;

  return (
    <Card data-testid={dataTestId ?? 'data-table-card'} {...props}>
      <Stack spacing={2}>
        {shouldRenderHeader && (
          <DataTableActions
            search={search}
            primaryAction={primaryAction}
            searchPlaceholder={searchPlaceholder}
          />
        )}

        <TableContainer>
          <Table stickyHeader={stickyHeader} sx={{ border: 'none' }}>
            <DataTableHeader
              columns={displayedColumns}
              activeSort={activeSort}
              onSort={handleSortRequest}
            />

            {isLoading && (
              <TableRowsLoader
                pageSize={loadingRowCount}
                columns={displayedColumns.length}
              />
            )}

            <DataTableBody
              columns={displayedColumns}
              sortedRows={sortedRows}
              getRowId={getRowId}
              isLoading={isLoading}
              shouldShowEmptyState={shouldShowEmptyState}
              emptyState={resolvedEmptyState}
            />
          </Table>
        </TableContainer>
      </Stack>

      {shouldShowPagination && cardPagination && (
        <CardPagination {...cardPagination} isDataTable />
      )}
    </Card>
  );
};

export default DataTableCard;
